import React, { ReactNode, useContext } from "react";
import { Table, useAlert, FastActionButton, useModal, ModalTypeEnum, useFilters } from "@wit/mpesa-ui-components";
import { AlertTypeEnum } from "@wit/mpesa-ui-components/lib/context/alert/alert.context";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { ThemeContext } from "styled-components";
import TablePagination from "../../../../shared/components/table-pagination/table-pagination-component";
import { LoadingText, IconContainer } from "../../../../shared/shared.styled";
import { TableButtonsContainer } from "../../../../shared/shared.utils";
import TriggerJobFilters from "./trigger-job-filters.component";
import SafaricomActivateModal from "../../../../shared/components/safaricom-activate-modal/safaricom-activate-modal.component";
import { IJob } from "../jobs-list.interface";
import { JobsListUtils } from "../jobs-list.utils";
import JobsListApi from "../jobs-list.api";
import LoadingComponent from "../../../../shared/components/loading-component/LoadingComponent";
import moment from "moment";
import { MIN_ELAPSED_MINUTES_TO_RE_TRIGGER_JOB } from "../jobs-list.constants";
import useCheckFeatureAvailable from "../../../../shared/hooks/use-check-available-feature";
import { FEATURES } from "../../../../shared/renderer.utils";
import { CheckIcon } from "@wit/mpesa-ui-components";

/**
 * Trigger job component
 */
const TriggerJob = () => {
  const [t] = useTranslation();
  const [showAlert, hideAlert, setAlertProps] = useAlert();
  const isSearchAvailable = useCheckFeatureAvailable(FEATURES.SEARCH_JOB_LIST);
  const [jobs, setJobs] = React.useState<IJob[]>([]);
  const [isFetchingJobs, setIsFetchingJobs] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(false);
  const themeContext = useContext(ThemeContext);
  const [page, setActivePage] = React.useState(1);
  const pageSize = 20;
  const [totalJobs, setTotalJobs] = React.useState(0);
  const [removeBlacklistModal, setRemoveBlacklistModal] = React.useState<ReactNode>();
  const [showTriggerJobModal, hideTriggerJobModal] = useModal(
    ModalTypeEnum.CustomModal,
    undefined,
    removeBlacklistModal,
  );
  const {
    filters,
    updateFilter,
    isFilterActive,
    clearFilter,
    resetFilters,
    getFilterValue,
    getFiltersQueryString,
    updateMultipleFilters,
  } = useFilters();

  const getJobs = () => {
    setIsFetchingJobs(true);
    JobsListApi.methods
      .getJobs()
      .finally(() => setIsFetchingJobs(false))
      .then(
        res => {
          const fetchedJobs: IJob[] = res.data.map(jobName => ({ name: jobName }));
          setTotalJobs(fetchedJobs.length);
          setJobs(fetchedJobs);
        },
        () => {
          setAlertProps({
            title: t("pages.tools.jobsList.getJobsError"),
            type: AlertTypeEnum.ERROR,
          });
          showAlert();
        },
      );
  };

  /**
   * Trigger job method
   * @param job
   */
  const triggerJob = (job: IJob) => {
    setIsLoading(true);
    const { canBeTriggered, remainingTimeInSeconds } = JobsListUtils.checkJobAvailability(job.name);
    if (canBeTriggered) {
      JobsListApi.methods
        .triggerJob(job.name)
        .finally(() => {
          setIsLoading(false);
          hideTriggerJobModal();
        })
        .then(
          () => {
            sessionStorage.setItem(job.name, moment().format("YYYY-MM-DD HH:mm:ss"));
            setAlertProps({
              title: t("pages.tools.jobsList.triggerJobSuccess"),
              type: AlertTypeEnum.SUCCESS,
            });
            showAlert();
          },
          () => {
            setAlertProps({
              title: t("pages.tools.jobsList.getJobsError"),
              type: AlertTypeEnum.ERROR,
            });
            showAlert();
          },
        );
    } else {
      const hasAMinuteElapsed = MIN_ELAPSED_MINUTES_TO_RE_TRIGGER_JOB * 60 - remainingTimeInSeconds >= 60;
      const remainingTime = hasAMinuteElapsed
        ? MIN_ELAPSED_MINUTES_TO_RE_TRIGGER_JOB - Math.floor(remainingTimeInSeconds / 60)
        : MIN_ELAPSED_MINUTES_TO_RE_TRIGGER_JOB * 60 - remainingTimeInSeconds;
      const timeLabel = hasAMinuteElapsed ? "minutesLabel" : "secondsLabel";

      setAlertProps({
        title: t("pages.tools.jobsList.triggerJobUnavailableError", {
          time: `${remainingTime} ${t(`pages.tools.jobsList.${timeLabel}`)}`,
        }),
        type: AlertTypeEnum.ERROR,
      });
      setIsLoading(false);
      hideTriggerJobModal();
      showAlert();
    }
  };

  /**
   * Handles the pagination change
   * @param pageNumber
   */
  const handlePageChange = (pageNumber: number) => {
    setActivePage(pageNumber);
  };

  /**
   * Modal to remove a number from blacklist
   * @param blockedTillNumber
   */
  const showTriggerJobModalHandler = (job: IJob) => {
    setRemoveBlacklistModal(
      <SafaricomActivateModal
        secondaryAction={() => hideTriggerJobModal()}
        primaryAction={() => triggerJob(job)}
        title={t("pages.tools.jobsList.modal.triggerJobListConfirmationTitle")}
        description={t("pages.tools.jobsList.modal.triggerJobListConfirmationMessage", {
          job: job.name,
        })}
        primaryBtnLabel={t("pages.tools.jobsList.modal.confirmationLabel")}
        primaryBtnId="remove-button"
        secondaryBtnId="cancel-button"
      />,
    );
    showTriggerJobModal();
  };

  /**
   * refresh results
   */
  const refreshResults = () => {
    getJobs();
  };

  React.useEffect(() => {
    getJobs();
  }, []);

  return (
    <>
      {isLoading && <LoadingComponent loadingText={t("pages.tools.jobsList.triggeringJobExecution")} />}
      {!isLoading && isSearchAvailable && (
        <TriggerJobFilters
          filters={filters}
          isFilterActive={isFilterActive}
          updateFilter={updateFilter}
          clearFilter={clearFilter}
          resetFilters={resetFilters}
          getFilterValue={getFilterValue}
          getFiltersQueryString={getFiltersQueryString}
          updateMultipleFilters={updateMultipleFilters}
          refreshResults={refreshResults}
        />
      )}
      {isFetchingJobs && !isLoading ? (
        <LoadingText>{t("commons.loadingResults")}</LoadingText>
      ) : (
        !isLoading && (
          <>
            <Table
              values={jobs}
              columns={JobsListUtils.getJobsColumns()}
              displayActions={job =>
                renderFastActions<IJob>(
                  job,
                  {
                    triggerJob: { onClick: showTriggerJobModalHandler },
                  },
                  themeContext,
                )
              }
            />
            {totalJobs > pageSize ? (
              <TablePagination
                handlePageChange={handlePageChange}
                totalItems={totalJobs}
                activePage={page}
                pageSize={pageSize}
              />
            ) : null}
          </>
        )
      )}
    </>
  );
};

/**
 * Row fast actions
 * @param value
 * @param buttonActions
 * @param themeContext
 * @returns
 */
const renderFastActions = <T extends IJob>(
  value: T,
  buttonActions: {
    triggerJob: { onClick: (row: T) => any };
  },
  themeContext: any,
) => {
  return (
    <TableButtonsContainer id={`trigger-job-${value.name}`}>
      <FastActionButton
        iconComponent={
          <IconContainer size={16} color={themeContext.palette.successGreen}>
            <CheckIcon />
          </IconContainer>
        }
        onClick={() => buttonActions.triggerJob.onClick(value)}
        label={i18next.t("pages.tools.jobsList.triggerJob.label")}
      />
    </TableButtonsContainer>
  );
};

export default TriggerJob;
